import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import ShieldCheckIcon from 'mdi-react/ShieldCheckIcon';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import {
  AUTH_SESSION_UPDATED_EVENT,
  getAuthSession,
} from '@/services/authService';
import {
  buildMenuConfig,
  flattenMenuConfig,
} from '@/containers/Layout/sidebar/Config/menuConfig';
import { preloadRoute } from '@/containers/App/routeLoaders';
import {
  colorBackground,
  colorBlue,
  colorBorder,
  colorHover,
  colorText,
  colorTextAdditional,
  colorWhite,
} from '@/utils/palette';
import { left, right } from '@/utils/directions';
import { TopbarSearchWrap } from './BasicTopbarComponents';

const normalizeValue = value => String(value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase();

const TopbarMenuSearch = () => {
  const history = useHistory();
  const location = useLocation();
  const searchRef = useRef(null);
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isTwoFaEnabled, setIsTwoFaEnabled] = useState(
    Number(getAuthSession()?.usuario?.twofa_enabled) === 1,
  );

  const menuEntries = useMemo(() => {
    const authSession = getAuthSession();
    const dynamicEntries = flattenMenuConfig(buildMenuConfig(authSession?.menu || [], authSession));
    const uniqueEntries = new Map();

    dynamicEntries.forEach((item) => {
      if (item?.route && !uniqueEntries.has(item.route)) {
        uniqueEntries.set(item.route, item);
      }
    });

    return Array.from(uniqueEntries.values());
  }, []);

  const filteredEntries = useMemo(() => {
    const normalizedQuery = normalizeValue(query.trim());

    if (!normalizedQuery) {
      return [];
    }

    return menuEntries.filter((item) => {
      const searchableText = normalizeValue([
        item.title,
        item.parents.join(' '),
        item.route,
      ].join(' '));

      return searchableText.includes(normalizedQuery);
    });
  }, [menuEntries, query]);

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (!searchRef.current || searchRef.current.contains(event.target)) {
        return;
      }

      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setQuery('');
  }, [location.pathname]);

  useEffect(() => {
    const handleSessionUpdated = (event) => {
      setIsTwoFaEnabled(Number(event?.detail?.usuario?.twofa_enabled) === 1);
    };

    window.addEventListener(AUTH_SESSION_UPDATED_EVENT, handleSessionUpdated);

    return () => {
      window.removeEventListener(AUTH_SESSION_UPDATED_EVENT, handleSessionUpdated);
    };
  }, []);

  const handleSelect = (route) => {
    if (!route) {
      return;
    }

    preloadRoute(route);
    history.push(route);
    setIsOpen(false);
    setQuery('');
  };

  const handleChange = (event) => {
    setQuery(event.target.value);
    setIsOpen(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
      return;
    }

    if (event.key === 'Enter' && filteredEntries.length > 0) {
      event.preventDefault();
      handleSelect(filteredEntries[0].route);
    }
  };

  return (
    <SearchArea ref={searchRef}>
      {isTwoFaEnabled ? (
        <TwoFaIndicator
          type="button"
          onClick={() => history.push('/pages/2fa')}
          title="Verificação em duas etapas ativa"
          aria-label="Verificação em duas etapas ativa"
        >
          <ShieldCheckIcon />
        </TwoFaIndicator>
      ) : null}

      <SearchInputWrap>
        <SearchIcon className="lnr lnr-magnifier" />
        <SearchInput
          type="text"
          value={query}
          placeholder="Buscar menu..."
          onChange={handleChange}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
        />
      </SearchInputWrap>

      {isOpen && query.trim() && (
        <SearchResults>
          {filteredEntries.length > 0 ? (
            filteredEntries.map(item => (
              <SearchResultButton
                key={item.key}
                type="button"
                onClick={() => handleSelect(item.route)}
              >
                <SearchResultIcon className={`lnr lnr-${item.icon}`} />
                <SearchResultContent>
                  <SearchResultTitle>{item.title}</SearchResultTitle>
                  <SearchResultMeta>
                    {item.parents.length > 0 ? item.parents.join(' > ') : 'Acesso rapido'}
                  </SearchResultMeta>
                </SearchResultContent>
              </SearchResultButton>
            ))
          ) : (
            <EmptyState>Nenhum menu encontrado.</EmptyState>
          )}
        </SearchResults>
      )}
    </SearchArea>
  );
};

export default TopbarMenuSearch;

const SearchArea = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 340px;
  margin-right: 0;

  @media screen and (max-width: 767px) {
    width: 260px;
  }

  @media screen and (max-width: 576px) {
    width: 200px;
  }
`;

const SearchInputWrap = styled(TopbarSearchWrap)`
  position: relative;
  flex: 1 1 auto;
  width: auto;
  align-items: center;
`;

const SearchIcon = styled.span`
  position: absolute;
  top: 50%;
  ${left}: 14px;
  transform: translateY(-50%);
  color: rgba(255, 255, 255, 0.72);
  font-size: 15px;
  pointer-events: none;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 42px;
  margin: auto 0;
  padding: 0 14px 0 40px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.12);
  color: ${colorWhite};
  font-size: 13px;
  transition: border-color 0.2s ease, background 0.2s ease;

  &::placeholder {
    color: rgba(255, 255, 255, 0.62);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.34);
    background: rgba(255, 255, 255, 0.18);
  }
`;

const TwoFaIndicator = styled.button`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  padding: 0;
  border: none;
  border-radius: 999px;
  background: rgba(16, 185, 129, 0.18);
  cursor: pointer;
  transition: background-color 0.2s ease;

  svg {
    width: 16px;
    height: 16px;
    fill: #6ee7b7;
  }

  &:hover {
    background: rgba(16, 185, 129, 0.26);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 0.18rem rgba(110, 231, 183, 0.2);
  }
`;

const SearchResults = styled.div`
  position: absolute;
  top: calc(100% + 10px);
  ${right}: 0;
  width: 360px;
  max-width: calc(100vw - 32px);
  background: ${colorBackground};
  border: 1px solid ${colorBorder};
  border-radius: 16px;
  box-shadow: 0 18px 36px rgba(25, 36, 56, 0.18);
  overflow: hidden;
  z-index: 130;

  @media screen and (max-width: 576px) {
    width: 300px;
  }
`;

const SearchResultButton = styled.button`
  width: 100%;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 13px 16px;
  border: none;
  background: transparent;
  text-align: ${left};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${colorHover};
  }
`;

const SearchResultIcon = styled.span`
  margin-top: 2px;
  color: ${colorBlue};
  font-size: 15px;
`;

const SearchResultContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

const SearchResultTitle = styled.span`
  color: ${colorText};
  font-size: 14px;
  font-weight: 600;
  line-height: 18px;
`;

const SearchResultMeta = styled.span`
  margin-top: 3px;
  color: ${colorTextAdditional};
  font-size: 12px;
  line-height: 16px;
  word-break: break-word;
`;

const EmptyState = styled.div`
  padding: 16px;
  color: ${colorTextAdditional};
  font-size: 13px;
  line-height: 18px;
`;
